import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { PropertyContext } from '../contexts/PropertyContext';
import { AuthContext } from '../contexts/AuthContext';
import { getPropertyTypeLabel } from '../utils/propertyUtils';
import PageWrapper from '../components/PageWrapper';
import CreatePropertyModal from '../components/CreatePropertyModal';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';

const Properties = () => {
  const { properties, loading, error, addProperty } = useContext(PropertyContext);
  const { user, currentAgent, agencyCurrency, agencyCountry } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filter properties based on search and type
  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || property.property_type === selectedType;
    return matchesSearch && matchesType;
  });

  // Get unique property types for filter
  const propertyTypes = [...new Set(properties.map(p => p.property_type))];

  const handleCreateProperty = async (propertyData) => {
    console.log('[Properties] handleCreateProperty called');
    console.log('[Properties] Current session status:', {
      agencyId: sessionStorage.getItem('agencyId'),
      token: sessionStorage.getItem('token') ? 'Present' : 'Missing',
      user: sessionStorage.getItem('user') ? 'Present' : 'Missing',
      currentAgent: currentAgent ? 'Present' : 'Missing'
    });
    return await addProperty(propertyData);
  };

  // Show loading state
  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Loading properties...</h3>
              <p className="mt-2 text-gray-500">Please wait while we fetch your properties</p>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-20">
              <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md mx-auto">
                <div className="text-red-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-red-900 mb-2">Error Loading Properties</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <button
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 flex items-center mx-auto"
                  onClick={() => window.location.reload()}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Agency Header */}
          {currentAgent && (
            <div className="mb-8">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center">
                  <div className="bg-blue-500 p-3 rounded-xl mr-4">
                    <BuildingOfficeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-900">{currentAgent.agency.name}</h2>
                    <p className="text-blue-700">
                      {currentAgent.agency.address} • {agencyCountry} • Currency: {agencyCurrency}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <div className="md:col-span-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MagnifyingGlassIcon className="w-4 h-4 inline mr-2" />
                  Search Properties
                </label>
                <input
                  type="text"
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  placeholder="Search by name or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="md:col-span-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <AdjustmentsHorizontalIcon className="w-4 h-4 inline mr-2" />
                  Filter by Type
                </label>
                <select
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  {propertyTypes.filter(Boolean).map((type, idx) => (
                    <option key={String(type) + '-' + idx} value={type}>
                      {getPropertyTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <button
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedType('all');
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-600">
              Showing {filteredProperties.length} of {properties.length} properties
              {currentAgent && (
                <span className="ml-2 inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {currentAgent.agency.name}
                </span>
              )}
            </p>
            {user && (
              <button
                className="flex items-center px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                onClick={() => setShowCreateModal(true)}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Property
              </button>
            )}
          </div>

          {/* Properties Grid */}
          {filteredProperties.length === 0 ? (
            <div className="text-center py-20">
              <BuildingOfficeIcon className="mx-auto h-16 w-16 text-gray-400" />
              <h3 className="mt-4 text-xl font-medium text-gray-900">No properties found</h3>
              <p className="mt-2 text-gray-500 mb-6">
                {properties.length === 0
                  ? "No properties available in your agency. Start by creating your first property."
                  : "Try adjusting your search criteria or clearing the filters."
                }
              </p>
              <div className="flex gap-3 justify-center">
                {properties.length > 0 && (
                  <button
                    className="px-4 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors duration-200"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedType('all');
                    }}
                  >
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset Filters
                  </button>
                )}
                {user && (
                  <button
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors duration-200"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <PlusIcon className="w-4 h-4 mr-2 inline" />
                    Create Property
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProperties.map(property => (
                <div key={property.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-emerald-300 transition-all duration-300 overflow-hidden group">
                  {/* Property Image */}
                  <div className="relative">
                    {property.property_image?.medium_url || property.property_image?.file_url || property.image ? (
                      <img
                        src={property.property_image?.medium_url || property.property_image?.file_url || property.image}
                        className="w-full h-48 object-cover"
                        alt={property.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"%3E%3Cpath strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"%3E%3C/path%3E%3C/svg%3E';
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}

                    {/* Property Type & Room Count Badges */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1">
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500 text-white shadow-lg">
                        {getPropertyTypeLabel(property.property_type)}
                      </span>
                      {property.property_type === 'multi_rental' && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-500 text-white shadow-lg">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          {property.room_count ?? 0} {(property.room_count ?? 0) === 1 ? 'Room' : 'Rooms'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Property Content */}
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors duration-200">
                        {property.name}
                      </h3>
                      <div className="flex items-center text-gray-500">
                        <MapPinIcon className="w-4 h-4 mr-1" />
                        <span className="text-sm">{property.address}</span>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <Link
                      to={`/properties/${property.id}`}
                      className="w-full flex items-center justify-center px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors duration-200 font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create Property Modal */}
          <CreatePropertyModal
            show={showCreateModal}
            onHide={() => setShowCreateModal(false)}
            onCreateProperty={handleCreateProperty}
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default Properties;
